import type { ComponentProps } from "react";
import { Tabs as TabsPrimitive } from "radix-ui";
import { segmentedItem, segmentedStrip } from "@/components/ui/segmented";
import { cn } from "@jbnl/shared-ui";

/**
 * shadcn's tabs, sectioning a detail pane that holds several subjects (ADR-0064). Imported from
 * the `radix-ui` umbrella this repo already depends on, so it costs no new dependency.
 */
export function Tabs({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      className={cn("flex flex-col gap-4", className)}
      {...props}
    />
  );
}

/**
 * The strip wraps and never scrolls sideways: four labels need about 596px against a detail pane
 * measuring 420px at `xl`, so the alternative is a tab hidden off the edge (ADR-0064).
 *
 * Drawn by `segmented`, which the accounts filter strip uses too - the two are different elements
 * on purpose and there was never a reason for them to look different.
 */
export function TabsList({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List className={cn(segmentedStrip, className)} {...props} />
  );
}

export function TabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(segmentedItem, className)}
      {...props}
    />
  );
}

/**
 * Panels stay mounted, because a hand-rolled form seeded at mount comes back blank (ADR-0064).
 * The hiding is ours: under `forceMount` Radix's `hidden` is always false. jsdom loads no
 * stylesheet, so a test still finds an inactive panel - drive the strip.
 */
export function TabsContent({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      forceMount
      className={cn("data-[state=inactive]:hidden", className)}
      {...props}
    />
  );
}
